// src/features/consultation/allergyAlerts.ts
// The patient's allergy record, rendered as alerts for the Consultation
// workspace's AlertStrip — the wireframe's marker 2, which replaces the
// 12px warning line that used to sit beside the tabs.
//
// This is a projection of rows, not a rules engine. It reads
// `patient_allergies` and restates what is already recorded there; it does
// not infer, score or cross-check anything. The wireframe is explicit that
// OPD safety alerts beyond the allergy record are a separate backend project
// (a deterministic OP rules engine), and this file must not quietly become
// one — anything that needs to reason across drugs belongs in
// backend/utils/rxSafetyChecks.js, which already exists and already surfaces
// through SafetyAlertBanner inside the prescription builder.
//
// The severity mapping is deliberately coarse, because the record's own
// vocabulary is: `criticality` has three values and only one of them
// ('high') asserts a risk of serious harm. 'low' and 'unable-to-assess' both
// mean "recorded, not established as high risk" — different from each other
// clinically, but not different enough to justify inventing a third alert
// tone the record cannot actually support.
import type { StripAlert } from '@/components/ui/AlertStrip';
import type { Allergy } from './types';
import { NO_KNOWN_ALLERGIES } from './useAllergyStatus';

function recordedOn(a: Allergy): string | null {
  const raw = a.onset_date || a.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * @param allergies every row for the patient, sentinel included — the
 *   sentinel is what distinguishes "asked, none found" from "nobody asked",
 *   so it must not be filtered out before it gets here.
 * @param reviewed false while the query is still loading or errored, where
 *   "no rows" means "not known yet" rather than "not reviewed". Saying
 *   nothing is the only safe rendering of an unloaded allergy record.
 */
export function buildAllergyAlerts(allergies: Allergy[] | undefined, reviewed: boolean): StripAlert[] {
  if (!reviewed || !allergies) return [];

  const active = allergies.filter(
    a => a.substance !== NO_KNOWN_ALLERGIES && a.clinical_status === 'active'
  );

  if (!active.length) {
    // A row saying "no known allergies" is an answer; no rows at all is not.
    // The prescriber is told which of the two they are looking at, and a
    // reviewed-and-clear record says nothing at all — an alert strip that
    // fires on every patient is one nobody reads.
    if (allergies.length) return [];
    return [{
      key: 'allergy:not-reviewed',
      label: 'Allergy history',
      severity: 'amber',
      message: 'Allergies not reviewed for this patient',
      detail: 'Confirm with the patient before prescribing.',
      // Deliberately the same three-valued verdict, and the same wording, as
      // PatientBanner's own allergy badge (useAllergyStatus) — the banner and
      // the strip must never disagree about the state of one record.
      basis: 'Nothing stands on this patient’s allergy record — neither a recorded allergy nor a “no known allergies” entry. (An allergy marked inactive or resolved does not count: GET /api/patients/:uhid/allergies returns active rows only.) Nobody having asked is not the same as there being nothing to find.',
      advisoryNote: 'Advisory only — recording an allergy, or affirming there are none, is done in the Allergies panel.',
    }];
  }

  return active.map(a => {
    const when = recordedOn(a);
    return {
      key: `allergy:${a.id}`,
      label: 'Allergy',
      severity: a.criticality === 'high' ? 'red' : 'amber',
      message: `${a.substance} allergy on record`,
      detail: [
        a.reaction ? `Reaction: ${a.reaction}` : null,
        a.category ? `Category: ${a.category}` : null,
        when ? `Recorded ${when}` : null,
      ].filter(Boolean).join(' · ') || 'Check before prescribing.',
      basis: [
        `Recorded in this patient's allergy record${when ? ` on ${when}` : ''}`,
        a.created_by ? `by ${a.created_by}` : null,
        `· verification: ${a.verification_status}`,
        `· criticality: ${a.criticality ?? 'not stated'}`,
        a.source === 'ai' ? '· entered from an AI-assisted draft and confirmed by a clinician' : null,
      ].filter(Boolean).join(' '),
      advisoryNote: 'Advisory only — this restates the recorded allergy, it does not check it against anything you prescribe. Drug-level checks run in the Prescription tab.',
    } satisfies StripAlert;
  });
}
